import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AppContext } from "../../../Context/AppContext";
import { isKycBlocked } from "../../../Constants/kycStatus";
import CustomCheckboxLabel from "../../CustomCheckboxLabel";
import CustomTooltipBox from "../../CustomTooltips/CustomTooltipBox";
import CustomTypography from "../../Typography";
import StepperTabs from "../../../Containers/ManagePricelists/Modals/PricelistModal/content/StepperTabs";
import TermsDialog from "../TermsDialog";
import DeleteDeactivatePricelistDialog from "../DeleteDeactivatePricelistDialog/DeleteDeactivatePricelistDialog";
import CancelOfferModel from "../CancelOfferModel";
import LeaveCommunityModal from "../../../Containers/MyCommunities/LeaveCommunityModal";
import MaximumCapacityReachedModal from "../MaximumCapacityReachedModal";
import {
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import {
  categoryNamespaces,
  SERVICE_CATEGORY_NAMESPACES,
} from "../../../Helpers/CategoryNamespaces";
import { useSnackbar } from "../../SnackBar";
import { sortByDisplayOrder } from "../../../Utils/SortData";
import muiCustomStyles from "./styles";

// Step identifiers. The wizard only shows a step for an item category the
// supplier actually has items in, so the visible steps are computed at render
// time from the loaded data rather than being a fixed Services -> Products pair.
const STEP_KEY_SERVICES = "services";
const STEP_KEY_PRODUCTS = "products";
const STEP_KEY_RESTAURANTS = "restaurants";

// Admin_system answers a join request with 409 + this code once the supplier
// has reached its members capacity (it also sends max_members).
const SUPPLIER_MEMBERS_CAPACITY_REACHED = "SUPPLIER_MEMBERS_CAPACITY_REACHED";

const SupplierJoinOffersModal = ({
  open,
  onClose,
  communityData,
  viewType,
  sendJoinRequest = false,
  onActionClick,
  cancelOffer,
  acceptJoinRequest,
  onLeaveCommunity,
}) => {
  const { t } = useTranslation(
    "supplier-join-offers-modal",
    "tooltip-sections"
  );
  const showSnackbar = useSnackbar();
  const { kycStatus } = useContext(AppContext);
  const kycBlocked = isKycBlocked(kycStatus);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Each step lists a different category tree, and the three trees live in
  // three different namespaces — so the row renderer is told which chain to
  // use rather than assuming the shared (service) one.
  const { t: tCategories } = useTranslation([
    "categories",
    "products_categories",
    "restaurants_categories",
  ]);

  const [openDeactivateDialog, setOpenDeactivateDialog] = useState(false);
  const [openCancelModel, setOpenCancelModel] = useState(false);
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [openLeaveModal, setOpenLeaveModal] = useState(false);
  // { maxClients, communityName } for the capacity popup; null while closed.
  // Kept out of resetOfferState, and the name is captured here, so the popup
  // survives the wizard closing (MyCommunities clears communityData on close).
  const [capacityReached, setCapacityReached] = useState(null);
  const [communityTOS, setCommunityTOS] = useState(null);
  const [showTermsCheckbox, setShowTermsCheckbox] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  // The wizard's steps are derived from the fetched lists, so until they land
  // there is no honest wizard to draw — see the body below.
  const [offersLoading, setOffersLoading] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);

  // Services step state
  const [pricelists, setPricelists] = useState([]);
  const [selectedPricelists, setSelectedPricelists] = useState([]);
  const [initialActivePricelists, setInitialActivePricelists] = useState([]);
  const [uncheckedActivePricelists, setUncheckedActivePricelists] = useState(
    []
  );
  const [discounts, setDiscounts] = useState({});

  // Products step state — mirrors the services shape
  const [productCategories, setProductCategories] = useState([]);
  const [selectedProductCategories, setSelectedProductCategories] = useState(
    []
  );
  const [initialActiveProductCategories, setInitialActiveProductCategories] =
    useState([]);
  const [
    uncheckedActiveProductCategories,
    setUncheckedActiveProductCategories,
  ] = useState([]);
  const [productDiscounts, setProductDiscounts] = useState({});

  // Restaurants step state — same shape again; the BE mirrors the tables.
  const [restaurantCategories, setRestaurantCategories] = useState([]);
  const [selectedRestaurantCategories, setSelectedRestaurantCategories] =
    useState([]);
  const [
    initialActiveRestaurantCategories,
    setInitialActiveRestaurantCategories,
  ] = useState([]);
  const [
    uncheckedActiveRestaurantCategories,
    setUncheckedActiveRestaurantCategories,
  ] = useState([]);
  const [restaurantDiscounts, setRestaurantDiscounts] = useState({});

  const [termsAccepted, setTermsAccepted] = useState(false);

  const { community_id, community_name, id, name } = communityData || {};
  const CommunityId = community_id || id;
  const CommunityName = community_name || name;

  // Only surface a step for a category the supplier actually has items in:
  // a supplier with no products must not get a Products step (and vice versa).
  const steps = [];
  if (pricelists.length > 0) {
    steps.push({ key: STEP_KEY_SERVICES, label: t("step_services") });
  }
  if (productCategories.length > 0) {
    steps.push({ key: STEP_KEY_PRODUCTS, label: t("step_products") });
  }
  if (restaurantCategories.length > 0) {
    steps.push({ key: STEP_KEY_RESTAURANTS, label: t("step_restaurants") });
  }
  const currentStepKey = steps[currentStep]?.key;
  const isProductsStep = currentStepKey === STEP_KEY_PRODUCTS;
  const isRestaurantsStep = currentStepKey === STEP_KEY_RESTAURANTS;
  // Both catalog steps list categories rather than pricelists.
  const isCatalogStep = isProductsStep || isRestaurantsStep;
  const currentStepItemCount = isProductsStep
    ? productCategories.length
    : isRestaurantsStep
      ? restaurantCategories.length
      : pricelists.length;

  // There is no "last step" until the steps themselves are known. While the
  // offer loads `steps` is empty and `0 >= -1` made this vacuously true, so
  // everything keyed on it — the terms consent row, the primary button's label,
  // the cancel-offer link — rendered its final-step form for a moment, vanished
  // once the real steps arrived, and came back at the end of the wizard.
  const isLastStep = !offersLoading && currentStep >= steps.length - 1;
  const hasAnySelection =
    selectedPricelists.length > 0 ||
    selectedProductCategories.length > 0 ||
    selectedRestaurantCategories.length > 0;
  const isFinalActionDisabled = !hasAnySelection || !termsAccepted;
  // Advancing between steps is never blocked: a supplier may offer only
  // products (or only services). The final join action enforces that at least
  // one item overall is selected via isFinalActionDisabled.

  const buildDiscountsPayload = (selectedIds, discountMap) =>
    selectedIds
      .map((categoryId) => {
        const raw = discountMap[categoryId];
        if (raw === undefined || raw === null || raw === "") return null;
        const numeric = Number(raw);
        if (!Number.isFinite(numeric)) return null;
        return { category_id: Number(categoryId), discount_rate: numeric };
      })
      .filter(Boolean);

  const getSupplierOffers = async () => {
    try {
      const res = sendJoinRequest
        ? await apiGetRequest(endpoints.getPricingSubcategories)
        : await apiGetRequest(endpoints.getSupplierOffers(CommunityId));

      let subcategories = res?.data?.subcategories || [];
      let productCats = res?.data?.product_categories || [];
      let restaurantCats = res?.data?.restaurant_categories || [];

      if (sendJoinRequest) {
        subcategories = sortByDisplayOrder(subcategories);
        productCats = sortByDisplayOrder(productCats);
        restaurantCats = sortByDisplayOrder(restaurantCats);
      }

      // Services
      const initialDiscounts = {};
      subcategories.forEach((item) => {
        initialDiscounts[item.id] =
          item.discount_rate != null ? String(Math.round(item.discount_rate)) : "";
      });
      setDiscounts(initialDiscounts);
      setPricelists(subcategories);
      if (viewType === "join") {
        setSelectedPricelists(subcategories.map((item) => item.id));
      } else {
        setSelectedPricelists(
          subcategories.filter((item) => item.is_offered).map((item) => item.id)
        );
      }
      setInitialActivePricelists(
        subcategories.filter((item) => item.is_active)
      );

      // Products
      const initialProductDiscounts = {};
      productCats.forEach((item) => {
        initialProductDiscounts[item.id] =
          item.discount_rate != null ? String(Math.round(item.discount_rate)) : "";
      });
      setProductDiscounts(initialProductDiscounts);
      setProductCategories(productCats);
      if (viewType === "join") {
        setSelectedProductCategories(productCats.map((item) => item.id));
      } else {
        setSelectedProductCategories(
          productCats.filter((item) => item.is_offered).map((item) => item.id)
        );
      }
      setInitialActiveProductCategories(
        productCats.filter((item) => item.is_active)
      );

      // Restaurants
      const initialRestaurantDiscounts = {};
      restaurantCats.forEach((item) => {
        initialRestaurantDiscounts[item.id] =
          item.discount_rate != null ? String(Math.round(item.discount_rate)) : "";
      });
      setRestaurantDiscounts(initialRestaurantDiscounts);
      setRestaurantCategories(restaurantCats);
      if (viewType === "join") {
        setSelectedRestaurantCategories(restaurantCats.map((item) => item.id));
      } else {
        setSelectedRestaurantCategories(
          restaurantCats.filter((item) => item.is_offered).map((item) => item.id)
        );
      }
      setInitialActiveRestaurantCategories(
        restaurantCats.filter((item) => item.is_active)
      );
    } catch (error) {
      showSnackbar(
        `${t("toast_get_offers_failed")} ${CommunityName}`,
        "error"
      );
      console.log("error...", error);
    }
  };

  const getCommunityTOS = async () => {
    try {
      const res = await apiGetRequest(endpoints.getCommunityTOS(CommunityId));
      setCommunityTOS(res?.data?.tos_supplier);
    } catch {
      showSnackbar(`${t("toast_get_tos_failed")} ${CommunityName}`, "error");
    }
  };

  const handleCheckboxChange = (pricelistId) => {
    setSelectedPricelists((prev) =>
      prev.includes(pricelistId)
        ? prev.filter((id) => id !== pricelistId)
        : [...prev, pricelistId]
    );
  };

  const handleDiscountChange = (pricelistId, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    const clamped =
      value === "" ? "" : String(Math.min(99, Math.max(0, Number(value))));
    setDiscounts((prev) => ({ ...prev, [pricelistId]: clamped }));
  };

  const handleProductCheckboxChange = (categoryId) => {
    setSelectedProductCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleProductDiscountChange = (categoryId, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    const clamped =
      value === "" ? "" : String(Math.min(99, Math.max(0, Number(value))));
    setProductDiscounts((prev) => ({ ...prev, [categoryId]: clamped }));
  };

  const handleRestaurantCheckboxChange = (categoryId) => {
    setSelectedRestaurantCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleRestaurantDiscountChange = (categoryId, value) => {
    if (value !== "" && !/^\d+$/.test(value)) return;
    const clamped =
      value === "" ? "" : String(Math.min(99, Math.max(0, Number(value))));
    setRestaurantDiscounts((prev) => ({ ...prev, [categoryId]: clamped }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleActionClick = async () => {
    if (!isLastStep) {
      handleNext();
      return;
    }

    if (isFinalActionDisabled) return;

    const uncheckedServices = initialActivePricelists.filter(
      (item) => !selectedPricelists.includes(item.id)
    );
    const uncheckedProducts = initialActiveProductCategories.filter(
      (item) => !selectedProductCategories.includes(item.id)
    );
    const uncheckedRestaurants = initialActiveRestaurantCategories.filter(
      (item) => !selectedRestaurantCategories.includes(item.id)
    );

    if (
      uncheckedServices.length > 0 ||
      uncheckedProducts.length > 0 ||
      uncheckedRestaurants.length > 0
    ) {
      setUncheckedActivePricelists(uncheckedServices);
      setUncheckedActiveProductCategories(uncheckedProducts);
      setUncheckedActiveRestaurantCategories(uncheckedRestaurants);
      setOpenDeactivateDialog(true);
      return;
    }

    await submitOffer();
    onActionClick?.();
    handleClose();
  };

  const submitOffer = async () => {
    try {
      setActionLoader(true);

      const categoryDiscounts = buildDiscountsPayload(
        selectedPricelists,
        discounts
      );
      const productCategoryDiscounts = buildDiscountsPayload(
        selectedProductCategories,
        productDiscounts
      );

      const restaurantCategoryDiscounts = buildDiscountsPayload(
        selectedRestaurantCategories,
        restaurantDiscounts
      );

      const sharedPayload = {
        active_subcategory_ids: selectedPricelists,
        category_discounts: categoryDiscounts,
        active_product_category_ids: selectedProductCategories,
        product_category_discounts: productCategoryDiscounts,
        active_restaurant_category_ids: selectedRestaurantCategories,
        restaurant_category_discounts: restaurantCategoryDiscounts,
      };

      if (sendJoinRequest) {
        await apiPostRequest(
          endpoints.joinCommunityRequest(CommunityId),
          sharedPayload
        );
      } else if (acceptJoinRequest) {
        await apiPostRequest(
          endpoints.acceptSupplierJoinRequest(CommunityId),
          sharedPayload
        );
      } else {
        await apiPutRequest(endpoints.updateSupplierOffer(CommunityId), {
          activeSubcategoryIds: selectedPricelists,
          category_discounts: categoryDiscounts,
          active_product_category_ids: selectedProductCategories,
          product_category_discounts: productCategoryDiscounts,
          active_restaurant_category_ids: selectedRestaurantCategories,
          restaurant_category_discounts: restaurantCategoryDiscounts,
        });
      }

      if (sendJoinRequest || viewType === "waiting") {
        showSnackbar(`${t("toast_request_sent_success")}`, "success");
      } else {
        showSnackbar(
          viewType === "join"
            ? `${CommunityName} ${t("toast_join_success_direct")}`
            : `${t("toast_save_success")} ${CommunityName}`,
          "success"
        );
      }
    } catch (error) {
      const data = error?.response?.data;
      if (data?.code === SUPPLIER_MEMBERS_CAPACITY_REACHED) {
        setCapacityReached({
          maxClients: data.max_members,
          communityName: CommunityName,
        });
        return;
      }
      showSnackbar(
        `${t("toast_submit_offer_failed")} ${CommunityName}`,
        "error"
      );
    } finally {
      setActionLoader(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    await submitOffer();
    setOpenDeactivateDialog(false);
    onActionClick?.();
    handleClose();
  };

  /**
   * Clears every per-community field. The modal is kept mounted by its parents
   * (MyCommunities, CommunityCard) with only an `open` prop, so anything left
   * behind here is still in state the next time it opens.
   *
   * This used to clear services and products but not restaurants, so reopening
   * the modal rendered the previous community's menu categories as the only
   * step — a restaurants-only wizard with the stepper hidden — until the fetch
   * landed and the real steps replaced it. Resetting the three verticals
   * together is what keeps a fourth from being forgotten the same way.
   */
  const resetOfferState = () => {
    setDiscounts({});
    setPricelists([]);
    setSelectedPricelists([]);
    setInitialActivePricelists([]);
    setUncheckedActivePricelists([]);

    setProductDiscounts({});
    setProductCategories([]);
    setSelectedProductCategories([]);
    setInitialActiveProductCategories([]);
    setUncheckedActiveProductCategories([]);

    setRestaurantDiscounts({});
    setRestaurantCategories([]);
    setSelectedRestaurantCategories([]);
    setInitialActiveRestaurantCategories([]);
    setUncheckedActiveRestaurantCategories([]);

    setTermsAccepted(false);
    setShowTermsCheckbox(false);
    setCommunityTOS(null);
    setCurrentStep(0);
  };

  const handleClose = () => {
    resetOfferState();
    onClose();
  };

  const handleOpenCancelModel = () => {
    setOpenCancelModel(true);
  };

  const handleCancelRequest = async () => {
    setActionLoader(true);
    await cancelOffer(communityData);
    setActionLoader(false);
    setOpenCancelModel(false);
    onClose();
  };

  /**
   * Declining the community's terms means leaving it — the supplier can't stay
   * in a community whose terms they refuse. It used to only close the TOS
   * dialog and untick the consent box, so the link read "leave community" and
   * did nothing. Leaving goes through the same confirmation every other exit
   * uses (reason required), and the parent owns the request so the communities
   * list refreshes the way it does for the other actions.
   */
  const handleRejectTerms = () => {
    setOpenTermsDialog(false);
    // Backing out of the confirmation leaves them where they were, minus the
    // consent they just declined — the save button stays gated until they
    // accept the terms.
    setTermsAccepted(false);
    setOpenLeaveModal(true);
  };

  const handleLeaveCommunity = async (_community, feedback) => {
    setActionLoader(true);
    try {
      await onLeaveCommunity?.(communityData, feedback);
    } catch {
      // The parent has already surfaced the failure — keep the confirmation up
      // rather than closing as though they had left.
      return;
    } finally {
      setActionLoader(false);
    }
    setOpenLeaveModal(false);
    handleClose();
  };

  useEffect(() => {
    if (communityData && open) {
      // Start from a clean slate rather than trusting the close path to have
      // cleared it — the modal stays mounted, so whatever is left in state is
      // what the supplier sees first.
      resetOfferState();
      // The consent box renders pre-accepted; only rejecting in the TOS dialog
      // clears it. Re-arming it on open is what lets the modal be saved a
      // second time — closing unsets it, and the [viewType] effect below only
      // re-runs when the view type itself changes.
      setTermsAccepted(true);
      // One flag for both requests: the first paint depends on the offer (which
      // steps exist) *and* on the community's TOS (whether the consent row
      // belongs on the last step). Releasing on the offer alone would still let
      // the consent row pop in a beat later. Both helpers swallow their own
      // errors, so this settles either way.
      setOffersLoading(true);
      Promise.all([getSupplierOffers(), getCommunityTOS()]).finally(() =>
        setOffersLoading(false)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityData, open]);

  useEffect(() => {
    setTermsAccepted(true);
  }, [viewType]);

  useEffect(() => {
    setShowTermsCheckbox(Boolean(communityTOS));
  }, [communityTOS]);

  const finalActionLabel =
    viewType === "view"
      ? t("actions_save_changes")
      : viewType === "waiting"
      ? t("actions_update_offer")
      : t("actions_join_community");

  const primaryButtonLabel = isLastStep ? finalActionLabel : t("button_next");
  // Belt and braces: the buttons that open this modal are already disabled
  // while the supplier's business verification is open, but this is the call
  // that actually reaches a community, so it refuses on its own too.
  const primaryButtonDisabled =
    offersLoading ||
    kycBlocked ||
    (isLastStep ? isFinalActionDisabled || actionLoader : actionLoader);

  const renderRowList = ({
    items,
    selectedIds,
    discountMap,
    onToggle,
    onDiscountChange,
    emptyStateKey,
    categoryNs = SERVICE_CATEGORY_NAMESPACES,
  }) => {
    if (items.length === 0) {
      return (
        <Box sx={{ py: 4, px: 2, textAlign: "center" }}>
          <Typography sx={{ color: "rgba(62, 63, 76, 0.6)", fontSize: "14px" }}>
            {t(emptyStateKey)}
          </Typography>
        </Box>
      );
    }
    return items.map((item) => {
      const isChecked = selectedIds.includes(item.id);
      return (
        <Box key={item.id} sx={muiCustomStyles.pricelistRow}>
          <Box sx={muiCustomStyles.pricelistLabel}>
            <CustomCheckboxLabel
              dark
              label={tCategories(item.name, {
                ns: categoryNs,
                defaultValue: item.name,
              })}
              checked={isChecked}
              onChange={() => onToggle(item.id)}
              formControlLabelStyle={{
                px: "16px",
                py: "9px",
                flex: 1,
                minWidth: 0,
              }}
              formControlLabelTextStyle={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                wordBreak: "break-word",
              }}
            />
          </Box>

          {item.is_active && viewType !== "waiting" && (
            <Box sx={muiCustomStyles.currentActiveChip}>
              <Typography sx={muiCustomStyles.currentActiveChipText}>
                {t("active_tag")}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              ...muiCustomStyles.discountInputBox,
              opacity: isChecked ? 1 : 0.4,
            }}
          >
            <TextField
              size="small"
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={discountMap[item.id] ?? ""}
              disabled={!isChecked}
              onChange={(e) => onDiscountChange(item.id, e.target.value)}
              sx={muiCustomStyles.discountTextField}
            />
            <Typography sx={muiCustomStyles.discountPercentSign}>%</Typography>
          </Box>
        </Box>
      );
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth="1200px"
        fullScreen={isSmallScreen}
        fullWidth
        sx={muiCustomStyles.dialogStyle}
      >
        <Box sx={muiCustomStyles.dialogBox}>
          <Box sx={muiCustomStyles.headerBox}>
            <CustomTypography
              type="subheading"
              props={{ color: "rgba(62, 63, 76, 1)", lineHeight: "38px" }}
            >
              {`${t("your_offer_to")} ${CommunityName}`}
            </CustomTypography>

            {/* A single category isn't a multi-step flow — hide the stepper
                and only show it once there are two or more steps. */}
            {steps.length > 1 && (
              <Box sx={muiCustomStyles.headerCenter}>
                <StepperTabs
                  activeStep={currentStep}
                  steps={steps.map((step) => step.label)}
                />
              </Box>
            )}

            <Box sx={muiCustomStyles.headerActions}>
              <Box sx={{ display: { xs: "none", sm: "flex" } }}>
                <CustomTooltipBox
                  sections={t("suppliers_join_offers_modal", {
                    returnObjects: true,
                    ns: "tooltip-sections",
                  })}
                />
              </Box>
              <IconButton onClick={handleClose}>
                <CloseIcon
                  sx={{
                    // Header background is white on every breakpoint, so the icon
                    // must stay dark — a white icon was invisible on mobile.
                    color: "rgba(68, 65, 81, 1)",
                    fontSize: "28px",
                  }}
                />
              </IconButton>
            </Box>
          </Box>

          <Box sx={muiCustomStyles.bodyBox}>
            {offersLoading ? (
              // Which steps exist — and so which heading and which list belong
              // on screen — is only known once the offer has loaded.
              <Box sx={muiCustomStyles.loadingBox}>
                <CircularProgress
                  size={28}
                  sx={{ color: "rgba(37, 124, 109, 1)" }}
                />
              </Box>
            ) : (
              <Box sx={muiCustomStyles.bodyInner}>
                <Typography sx={muiCustomStyles.heading}>
                  {currentStepKey === STEP_KEY_RESTAURANTS
                    ? `${t("pick_restaurants_and_discounts")} ${CommunityName}`
                    : currentStepKey === STEP_KEY_PRODUCTS
                      ? `${t("pick_products_and_discounts")} ${CommunityName}`
                      : `${t("pick_pricelists_and_discounts")} ${CommunityName}`}
                </Typography>

                <Box sx={muiCustomStyles.scrollListWrapper}>
                  {currentStepItemCount > 0 && (
                    <Box sx={muiCustomStyles.listHeaderRow}>
                      <Typography sx={muiCustomStyles.listHeaderSelection}>
                        {isCatalogStep
                          ? t("list_header_categories")
                          : t("list_header_pricelists")}
                      </Typography>
                      <Typography sx={muiCustomStyles.listHeaderDiscount}>
                        {t("list_header_discount")}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={muiCustomStyles.scrollList}>
                    {currentStepKey === STEP_KEY_RESTAURANTS
                      ? renderRowList({
                          items: restaurantCategories,
                          selectedIds: selectedRestaurantCategories,
                          discountMap: restaurantDiscounts,
                          onToggle: handleRestaurantCheckboxChange,
                          onDiscountChange: handleRestaurantDiscountChange,
                          emptyStateKey: "no_products_available",
                          categoryNs: categoryNamespaces(STEP_KEY_RESTAURANTS),
                        })
                      : currentStepKey === STEP_KEY_PRODUCTS
                      ? renderRowList({
                          items: productCategories,
                          selectedIds: selectedProductCategories,
                          discountMap: productDiscounts,
                          onToggle: handleProductCheckboxChange,
                          onDiscountChange: handleProductDiscountChange,
                          emptyStateKey: "no_products_available",
                          categoryNs: categoryNamespaces(STEP_KEY_PRODUCTS),
                        })
                      : renderRowList({
                          items: pricelists,
                          selectedIds: selectedPricelists,
                          discountMap: discounts,
                          onToggle: handleCheckboxChange,
                          onDiscountChange: handleDiscountChange,
                          emptyStateKey: "no_products_available",
                        })}
                  </Box>
                  <Box sx={muiCustomStyles.scrollFade} />
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={muiCustomStyles.footerBox}>
            {/* The consent row only exists on the last step, so it goes above
                the stepper: put it between the stepper and the buttons and the
                stepper visibly jumps up the moment the supplier reaches that
                step. Ordered this way the stepper stays put, directly above the
                buttons, for the whole wizard. On md+ the stepper is hidden and
                this is simply the left-hand half of a single footer row. */}
            {isLastStep && showTermsCheckbox && (
              <Box sx={muiCustomStyles.termsRow}>
                <CustomCheckboxLabel
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <Typography sx={muiCustomStyles.termsText}>
                  {t("i_accept_tos_part_1")} {CommunityName}{" "}
                  <Typography
                    component="span"
                    sx={muiCustomStyles.termsLink}
                    onClick={() => setOpenTermsDialog(true)}
                  >
                    {t("i_accept_tos_part_2")}
                  </Typography>
                </Typography>
              </Box>
            )}

            {/* Same guard as the header stepper: a single category isn't a
                multi-step flow. Below md the header has no room for it, so the
                stepper sits here, on its own row above the action buttons. */}
            {steps.length > 1 && (
              <Box sx={muiCustomStyles.footerStepper}>
                <StepperTabs
                  activeStep={currentStep}
                  steps={steps.map((step) => step.label)}
                />
              </Box>
            )}

            <Box sx={muiCustomStyles.footerActions}>
              {viewType === "waiting" && isLastStep && (
                <Typography
                  color="rgba(62, 63, 76, 1)"
                  sx={muiCustomStyles.cancelOfferText}
                  onClick={handleOpenCancelModel}
                >
                  {t("actions_cancel_offer")}
                </Typography>
              )}

              {currentStep > 0 && (
                <Button
                  variant="outlined"
                  sx={{
                    ...muiCustomStyles.actionButtonStyle,
                    backgroundColor: "transparent",
                    color: "rgba(62, 63, 76, 1)",
                    border: "1px solid rgba(62, 63, 76, 0.3)",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                    mr: "8px",
                  }}
                  onClick={handleBack}
                  disabled={actionLoader}
                >
                  {t("button_back")}
                </Button>
              )}

              <Button
                variant="contained"
                sx={muiCustomStyles.actionButtonStyle}
                disabled={primaryButtonDisabled}
                onClick={handleActionClick}
              >
                {primaryButtonLabel}
                {actionLoader && (
                  <CircularProgress
                    size={20}
                    color="inherit"
                    sx={{ ml: "8px" }}
                  />
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {openDeactivateDialog &&
        (uncheckedActivePricelists.length > 0 ||
          uncheckedActiveProductCategories.length > 0 ||
          uncheckedActiveRestaurantCategories.length > 0) && (
          <DeleteDeactivatePricelistDialog
            open={openDeactivateDialog}
            onClose={() => setOpenDeactivateDialog(false)}
            onConfirm={handleDeactivateConfirm}
            pricelist={[
              ...uncheckedActivePricelists,
              ...uncheckedActiveProductCategories,
              ...uncheckedActiveRestaurantCategories,
            ]}
            actionLoader={actionLoader}
            CommunityName={CommunityName}
          />
        )}

      {openCancelModel && (
        <CancelOfferModel
          open={openCancelModel}
          communityData={communityData}
          handleClose={() => setOpenCancelModel(false)}
          handleCancelRequest={handleCancelRequest}
          dialogTitle={t("cancel_offer_title")}
          dialogHeading={`${t("cancel_invite_heading")} ${CommunityName}`}
          isLoading={actionLoader}
        />
      )}

      <TermsDialog
        openDialog={openTermsDialog}
        setOpenDialog={setOpenTermsDialog}
        dialogTitle={t("community_tos_title", {
          ns: "tos-modal",
          community_name: CommunityName,
        })}
        termsDescription={communityTOS}
        showActions={true}
        onAccept={() => {
          setOpenTermsDialog(false);
          setTermsAccepted(true);
        }}
        {...(viewType === "join" || !onLeaveCommunity
          ? {}
          : { onReject: handleRejectTerms })}
        dialogPaperStyles={{ maxHeight: "456px" }}
      />

      <LeaveCommunityModal
        supplier={communityData}
        open={openLeaveModal}
        handleClose={() => setOpenLeaveModal(false)}
        handleOk={handleLeaveCommunity}
        // A supplier still waiting on approval is removing an offer, not
        // walking out of a community they work in — same wording the
        // waiting-approval tab already uses.
        value={viewType === "waiting" ? "approval" : "active"}
      />

      <MaximumCapacityReachedModal
        open={capacityReached !== null}
        onClose={() => setCapacityReached(null)}
        maxClients={capacityReached?.maxClients}
        communityName={capacityReached?.communityName}
      />
    </>
  );
};

export default SupplierJoinOffersModal;
